import {
    testIsArray,
    testIsNonEmptyString,
    testIsNotNull,
    testIsObject,
    testIsTrueish,
} from '../../lib/test-and-assert/test-base';
import { userManager } from '../user/user-manager';
import { isCookieSet } from '../../qaamgo/helper/cookie';
import { LimitData } from '../limits/new/limit-data';
import { eventBus } from '../../lib/event-bus/event-bus';
import { TOAST_SHOW, TOASTS } from '../../qaamgo/modules/sat/components/toasts/toast-consts';
import { projectHelper } from '../misc/project-helper';
import { ApiJob } from '../../model/converter/api-job';
import { initResultInlineToasts } from './util/main-result-inline-toasts';
import { useLimitsStore } from '../store/modules/limits-store';
import { useJobStore } from '../store/modules/job-store';

const COOKIE_RESULT_LIMIT_HINT_DISMISSED = 'qg_result_limit_hint_dismiss';
const RESULT_PDFA_JOB_GONE = 'RESULT_PDFA_JOB_GONE';
const RESULT_PDFA_VALIDATION_FAILED = 'RESULT_PDFA_VALIDATION_FAILED';

export { COOKIE_RESULT_LIMIT_HINT_DISMISSED };
export { RESULT_PDFA_JOB_GONE };
export { RESULT_PDFA_VALIDATION_FAILED };

/**
 * @param {RawJobInfo} rawJobInfo
 */
function initResultLimits(rawJobInfo) {
    if (isCookieSet(COOKIE_RESULT_LIMIT_HINT_DISMISSED)) {
        return;
    }

    const operation = rawJobInfo?.sat?.operation;

    if (!testIsNonEmptyString(operation)) {
        return;
    }

    const jobStore = useJobStore();
    jobStore.setOperation(operation);

    const constraints = rawJobInfo?.sat?.constraints;

    if (testIsNotNull(constraints) && testIsObject(constraints)) {
        jobStore.setConstraints(constraints);
    }
}

export { initResultLimits };

async function handleResultLimitToast() {
    // make sure we have the latest /me data with the latest constraints status
    const userData = await userManager.getUser();
    const meData = userData._rawMeData

    let limitsList = [];

    if (testIsArray(meData.constraints_status)) {
        meData.constraints_status.forEach((/** RawConstraintStatus */ constraintStatus) => {
            limitsList.push(LimitData.createFromConstraintStatus(constraintStatus));
        });
    }

    const limitsStore = useLimitsStore();
    limitsStore.addLimitsRefresh(limitsList);

    let user = await userManager.getUser();

    if (user.isPremium()) {
        return;
    }

    eventBus.emit(TOAST_SHOW, {
        name: TOASTS.TOAST_RESULT_LIMITS,
        data: null,
    });
}

export { handleResultLimitToast };

/**
 * @param {ApiJob} job
 */
async function handleRemoveBackgroundToast(job) {
    if (!projectHelper.isImg2go()) {
        return;
    }

    const conversion = job.conversion[0];

    if (conversion?.target !== 'png') {
        return;
    }

    if (job?.sat?.operation && (job.sat.operation.includes('compress') || job.sat.operation.includes('resize'))) {
        // Don't show toast for compress or resize features because they don't have all the options
        return;
    }

    // remove background option was set - we may assume that user already knows what they're doing
    if (conversion?.options?.alpha_channel === 'auto_create') {
        return;
    }

    let hasOpaqueInput = false;
    for (const [idx, /** ApiInput */ input] of Object.entries(job.input)) {
        if (testIsTrueish(input.metadata?.opaque)) {
            hasOpaqueInput = true;

            break;
        }
    }

    if (!hasOpaqueInput) {
        return;
    }

    await initResultInlineToasts();

    eventBus.emit(TOAST_SHOW, {
        name: TOASTS.TOAST_RESULT_REMOVE_BACKGROUND,
        data: job,
    });
}

async function handleResizeImageToast(job) {
    if (!projectHelper.isImg2go()) {
        return;
    }

    const conversion = job.conversion[0];
    const input = job.input[0];

    const targetWidth = conversion.options.width;
    const targetHeight = conversion.options.height;

    const sourceWidth = input.metadata.image_width;
    const sourceHeight = input.metadata.image_height;

    if (sourceHeight >= targetHeight || sourceWidth >= targetWidth) {
        return;
    }

    await initResultInlineToasts();

    eventBus.emit(TOAST_SHOW, {
        name: TOASTS.TOAST_RESULT_RESIZE_IMAGE,
        data: job,
    });
}

async function handlePdfaConversionResultToast(job) {
    if (job?.status?.code === 'failed') {
        return;
    }

    eventBus.emit(TOAST_SHOW, {
        name: TOASTS.TOAST_RESULT_PDFA_CONVERSION,
        data: job,
    });
}

async function handlePdfValidationResultToast(job) {
    eventBus.emit(TOAST_SHOW, {
        name: TOASTS.TOAST_RESULT_PDFA_VALIDATION,
        data: job,
    });
}

async function handleOcrHintToast(job) {
    const conversion = job.conversion[0];
    const inputs = job.input;
    const operation = job?.sat?.operation;

    if (!projectHelper.isPdf2go() && !projectHelper.isOnlineConvert()) {
        return;
    }

    if (
        operation !== 'convertPdfToWord' &&
        operation !== 'converttoword' &&
        operation !== 'toolpdftoword' &&
        operation !== 'converttodocx'
    ) {
        return;
    }

    if (conversion?.category !== 'document') {
        return;
    }

    if (conversion?.options?.ocr === true) {
        return;
    }

    let textPagePercentage = 0;
    inputs.forEach((input) => {
        textPagePercentage += input.metadata.textpages_percentage;
    });

    let averageTextPagePercentage = textPagePercentage / inputs.length;

    if (averageTextPagePercentage > 30) {
        return;
    }

    eventBus.emit(TOAST_SHOW, {
        name: TOASTS.TOAST_RESULT_OCR_HINT,
        data: job,
    });
}

export { handleRemoveBackgroundToast };
export { handlePdfaConversionResultToast };
export { handlePdfValidationResultToast };
export { handleOcrHintToast };
export { handleResizeImageToast };
